import { Fragment, useState } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { motion } from "motion/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Card } from "../../ui/Card";
import { Button } from "../../ui/Button";
import { PageSpinner } from "../../ui/Spinner";
import { Badge, initials } from "./tabs/_ui";
import { OverviewTab } from "./tabs/OverviewTab";
import { BillingTab } from "./tabs/BillingTab";
import { PaymentsTab } from "./tabs/PaymentsTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import { UsersTab } from "./tabs/UsersTab";
import { LicenseTab } from "./tabs/LicenseTab";
import { BackupTab } from "./tabs/BackupTab";
import { AuditTab } from "./tabs/AuditTab";
import { ContratsTab } from "./tabs/ContratsTab";
import { FacturesHubblotTab } from "./tabs/FacturesHubblotTab";

type TabKey =
  | "overview"
  | "abonnement"
  | "facturation"
  | "users"
  | "documents"
  | "systeme";

const TAB_GROUPS = [
  {
    label: "Données client",
    items: [
      { key: "overview", label: "Vue d'ensemble" },
      { key: "users", label: "Utilisateurs" },
      { key: "documents", label: "Documents" },
    ],
  },
  {
    label: "Gestion MyVictor",
    items: [
      { key: "abonnement", label: "Abonnement" },
      { key: "facturation", label: "Facturation" },
      { key: "systeme", label: "Système" },
    ],
  },
] as const satisfies ReadonlyArray<{
  label: string;
  items: ReadonlyArray<{ key: TabKey; label: string }>;
}>;

const STATUT: Record<string, { label: string; tone: "emerald" | "amber" | "slate" }> = {
  actif: { label: "Actif", tone: "emerald" },
  suspendu: { label: "Suspendu", tone: "amber" },
  resilie: { label: "Résilié", tone: "slate" },
};

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>("overview");

  const detailQ = useQuery({
    queryKey: ["tenant-detail", id],
    queryFn: () => api.tenants.get(id!),
    enabled: !!id,
  });

  const updateMut = useMutation({
    mutationFn: (statut: "actif" | "suspendu" | "resilie") =>
      api.tenants.update(id!, { statut }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      void queryClient.invalidateQueries({ queryKey: ["tenant-detail", id] });
    },
  });

  if (!id) {
    navigate("/tenants");
    return null;
  }

  if (detailQ.isLoading) return <PageSpinner />;
  const tenant = detailQ.data;
  if (!tenant) {
    return (
      <Card>
        <p className="text-sm text-navy-600">Agence introuvable.</p>
        <Link to="/tenants" className="text-sm text-accent-500 hover:underline">
          ← Retour à la liste
        </Link>
      </Card>
    );
  }

  const st = STATUT[tenant.statut] ?? STATUT.actif;

  return (
    <div className="space-y-5">
      <Link
        to="/tenants"
        className="inline-flex items-center gap-1 text-xs font-semibold text-navy-500 transition-colors hover:text-navy-800"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        Agences
      </Link>

      {/* Header agence */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white p-5 ring-1 ring-navy-100 shadow-sm"
      >
        <div className="flex items-center gap-4">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy-100 text-sm font-bold text-navy-700">
            {initials(tenant.raisonSociale)}
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy-800 sm:text-3xl">
              {tenant.raisonSociale}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-navy-500">
              <Badge tone={st!.tone} dot>
                {st!.label}
              </Badge>
              <span>·</span>
              <span>Plan {tenant.plan.toUpperCase()}</span>
              <span>·</span>
              <span>depuis le {new Date(tenant.createdAt).toLocaleDateString("fr-FR")}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {tenant.statut === "actif" ? (
            <Button
              variant="secondary"
              onClick={() => updateMut.mutate("suspendu")}
              disabled={updateMut.isPending}
            >
              Suspendre
            </Button>
          ) : null}
          {tenant.statut === "suspendu" ? (
            <Button
              onClick={() => updateMut.mutate("actif")}
              disabled={updateMut.isPending}
            >
              Réactiver
            </Button>
          ) : null}
          {tenant.statut !== "resilie" ? (
            <Button
              variant="danger"
              onClick={() => {
                if (
                  window.confirm(
                    `Résilier définitivement ${tenant.raisonSociale} ? Cette action est irréversible.`,
                  )
                ) {
                  updateMut.mutate("resilie");
                }
              }}
              disabled={updateMut.isPending}
            >
              Résilier
            </Button>
          ) : null}
        </div>
      </motion.div>

      {/* Onglets — 2 groupes (données client / gestion), sticky + scroll */}
      <div className="sticky top-0 z-10 -mx-2 overflow-x-auto px-2 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex w-max items-stretch gap-3">
          {TAB_GROUPS.map((group, gi) => (
            <Fragment key={group.label}>
              {gi > 0 ? (
                <span aria-hidden="true" className="my-1 w-px self-stretch bg-navy-200" />
              ) : null}
              <div className="flex items-center gap-1 rounded-2xl bg-white p-1.5 ring-1 ring-navy-100 shadow-sm">
                <span className="px-2 text-[10px] font-bold uppercase tracking-wider text-navy-400">
                  {group.label}
                </span>
                {group.items.map((it) => {
                  const active = tab === it.key;
                  return (
                    <button
                      key={it.key}
                      type="button"
                      onClick={() => setTab(it.key)}
                      className={`rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors ${
                        active
                          ? "bg-accent-600 text-white shadow-sm"
                          : "text-navy-600 hover:bg-navy-100"
                      }`}
                    >
                      {it.label}
                    </button>
                  );
                })}
              </div>
            </Fragment>
          ))}
        </div>
      </div>

      <div className="pt-2">
        {tab === "overview" ? <OverviewTab tenant={tenant} /> : null}
        {tab === "abonnement" ? (
          <div className="space-y-6">
            <BillingTab tenantId={id} />
            <LicenseTab tenantId={id} />
          </div>
        ) : null}
        {tab === "facturation" ? (
          <div className="space-y-6">
            <PaymentsTab tenantId={id} />
            <FacturesHubblotTab tenantId={id} />
            <ContratsTab tenantId={id} />
          </div>
        ) : null}
        {tab === "users" ? <UsersTab tenantId={id} /> : null}
        {tab === "documents" ? <DocumentsTab tenantId={id} /> : null}
        {tab === "systeme" ? (
          <div className="space-y-6">
            <BackupTab tenantId={id} raisonSociale={tenant.raisonSociale} />
            <AuditTab tenantId={id} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
